import { useState, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import Voice, { SpeechErrorEvent, SpeechResultsEvent } from '@react-native-voice/voice';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Importación dinámica segura para evitar errores de resolución en Web
const RNPermissions = Platform.OS === 'android' ? require('react-native').PermissionsAndroid : null;

// Tipos de error admitidos
export type VoiceErrorType = 'NETWORK_ERROR' | 'WEB_ERROR' | 'CLIENT' | 'OTHER' | null;

interface UseVoiceDictationReturn {
  isListening: boolean;
  startListening: (onResult?:(text:string)=>void) => Promise<void>;
  stopListening: () => Promise<void>;
  errorType: VoiceErrorType;
  isSupported: Promise<boolean>;
  clearError: () => void;
}

// Declaración para que TS reconozca la API de voz del navegador
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}
let globalSetIsListening: ((val: boolean) => void) | null = null;
let globalOnResultCallback: ((t: string) => void) | null = null;
let globalIsForceDisabled = false;
const forceDisabledListeners = new Set<(v: boolean) => void>();

const updateGlobalForceDisabled = (val: boolean) => {
    globalIsForceDisabled = val;
    forceDisabledListeners.forEach(l => l(val));
    AsyncStorage.setItem('VOICE_FORCE_DISABLED', 'true');
};

export const useVoiceDictation = (onResult: (text: string) => void): UseVoiceDictationReturn => {
  const [isListening, setIsListening] = useState<boolean>(false);
  const [isAvailable, setIsAvailable] = useState<boolean>(false);
  const [errorType, setErrorType] = useState<VoiceErrorType>(null);
  const [isForceDisabled, setIsForceDisabled] = useState<boolean>(globalIsForceDisabled);
  const webRecognitionRef = useRef<any>(null);

  useEffect(() => {
    // Suscribirse a cambios globales para que todos los elementos se refresquen
    const listener = (v: boolean) => setIsForceDisabled(v);
    forceDisabledListeners.add(listener);

    // Cargar estado inicial persistente solo si no se ha cargado ya globalmente
    if (!globalIsForceDisabled) {
        AsyncStorage.getItem('VOICE_FORCE_DISABLED').then(val => {
            if (val === 'true') updateGlobalForceDisabled(true);
        });
    }
    
    if (Platform.OS === 'web') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      
      if (SpeechRecognition && !webRecognitionRef.current) {
        const recognition = new SpeechRecognition();
        recognition.lang = 'es-ES';
        recognition.continuous = false; 
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;
        recognition.onstart = () => setIsListening(true);
        recognition.onend = () => setIsListening(false);
        recognition.onerror = () => {
            setIsListening(false);
            setErrorType('WEB_ERROR');
        };
        
        webRecognitionRef.current = recognition;
      }
    } else {
      // --- Lógica Nativa ---
      Voice.onSpeechStart = () => {
        if (globalSetIsListening) globalSetIsListening(true);
        setErrorType(null);
      };
      
      Voice.onSpeechEnd = () => {
          if (globalSetIsListening) globalSetIsListening(false);
        }
         AsyncStorage.setItem('VOICE_FORCE_DISABLED', 'false');
      Voice.onSpeechError = (e: SpeechErrorEvent) => {
        if (globalSetIsListening) globalSetIsListening(false);
        console.log("error DIC", e);
        
        // Si es error 5 (Client side error), desactivamos permanentemente para este dispositivo
        if (e.error?.code === '5') {
            updateGlobalForceDisabled(true);
            setErrorType('CLIENT');
        } else if (e.error?.code === '7' || e.error?.message?.includes("network")) {
          setErrorType('NETWORK_ERROR');
        } else {
          setErrorType('OTHER');
        }
      };
    }
    if(Platform.OS !== 'web' && typeof Voice !== 'undefined' && Voice?.isAvailable){
        Voice.isAvailable()?.then?.((_isAvailable) => {
        setIsAvailable(_isAvailable==1?true:false)
      })
    }

    // Limpieza al desmontar el componente
    return () => {
      forceDisabledListeners.delete(listener);
      if (Platform.OS !== 'web') {
        Voice.destroy().then(Voice.removeAllListeners);
      } else {
        if (webRecognitionRef.current) {
            webRecognitionRef.current.abort();
        }
      }
    };
  }, []); // Se ejecuta solo una vez al montar el componente

  // Función para pedir permiso en Android de forma segura
  const requestAndroidPermission = async (): Promise<boolean> => {
    if (Platform.OS !== 'android' || !RNPermissions) return true;
    try {
      const hasPermission = await RNPermissions.check(RNPermissions.PERMISSIONS.RECORD_AUDIO);
      if (hasPermission) return true;

      const status = await RNPermissions.request(
        RNPermissions.PERMISSIONS.RECORD_AUDIO,
        {
          title: "Permiso de Micrófono",
          message: "Se requiere acceso al micrófono para el dictado de voz.",
          buttonPositive: "OK",
        }
      );
      return status === 'granted';
    } catch (err) {
      console.warn("Permission Error:", err);
      return false;
    }
  };

    const startListening = async (onResult: (text: string) => void) => {
        setErrorType(null);
        globalOnResultCallback=onResult;
        globalSetIsListening = setIsListening;
        globalSetIsListening(true);
        if (Platform.OS === 'web') {
            if (webRecognitionRef.current) {
                try {
                    webRecognitionRef.current.onresult = (event: any) => {
                        const text = event.results[0][0].transcript;
                        if (globalOnResultCallback) globalOnResultCallback(text);
                    };
                    webRecognitionRef.current.start();
                } catch (e) {
                    console.warn("SpeechRecognition already started or error:", e);
                }
            }
        } else {
            const granted = await requestAndroidPermission();
            if (!granted) return;

            try {

                await Voice.stop();
                Voice.removeAllListeners();
                Voice.onSpeechResults = (e: SpeechResultsEvent) => {
                if (e.value && e.value.length > 0 && globalOnResultCallback) {
                    globalOnResultCallback(e.value[0]);
                }
            };
                await Voice.start('es-US', {
                    "EXTRA_PARTIAL_RESULTS": true,
                    "EXTRA_PREFER_OFFLINE": true,  // Nota el "PREFER"
                    
                });
            } catch (e) {
                console.error("Voice start error:", e);
            }
        }
    };

  const stopListening = async () => {
    if (Platform.OS === 'web') {
      if (webRecognitionRef.current) webRecognitionRef.current.stop();
    } else {
      try {
        await Voice.stop();
        setIsListening(false);
      } catch (e) {
        console.error("Voice stop error:", e);
      }
    }
  };

  
  
  return {
    isListening,
    startListening,
    stopListening,
    errorType,
    isSupported: ((Platform.OS === 'web' 
        ? !!(window.SpeechRecognition || window.webkitSpeechRecognition) 
        :  isAvailable)) && !isForceDisabled,
    clearError: () => setErrorType(null)
  };
};